import { Route, Routes } from 'react-router';
import DoiPanel from './components/DoiPanel';
import { useEffect, useState } from 'react';
import { Divider, Grid, Link, Stack, Typography } from '@mui/material';
import LoginForm from './components/LoginForm';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { scrollToSection } from './ScrollUtils';
import RetractionPerYearLinearCharts from './components/RetractionPerYearLinearChart';
import PublicJournalSummaryTable from './components/PublicJournalSummaryTable';
import { useSearchParams } from 'react-router-dom';
import ResponsiveAppBar from './components/ResponsiveAppBar';
import HistoricalDashboard from './components/HistoricalDashboard';
import PublisherSummaryBarChart from './components/PublisherSummaryBarChart';
import { useAuth } from './contexts/authContext';
import RegistrationPage from './components/RegistrationPage';
import LoginSideContent from './components/LoginSideContent';
import ArgosDashboardPage from './ArgosDashboardPage';
import SubscriptionPage from './components/payments/SubscriptionPage';
import SubscribeForm from './components/SubscribeForm';
import ReferenceAnalyzer from './components/authoring-tools/ReferenceAnalyser';
import { useVersionCheck } from './components/utils/VersionPollingHook';

function Copyright() {
  return (
    <Typography variant="body2" color="text.secondary" mt={1}>
      {'Copyright © '}
      <Link href="https://www.scitility.com/">Scitility&nbsp;PBC&nbsp;</Link>
      {new Date().getFullYear()}
      <Divider component='span' orientation="vertical" variant="middle" flexItem style={{ marginRight: 5, marginLeft: 5 }} />
      <Link href="mailto:hello@scitility.com">
        Contact us
      </Link>
      <Divider component='span' orientation="vertical" variant="middle" flexItem style={{ marginRight: 5, marginLeft: 5 }} />
      <Link target="_blank" href="https://app.termly.io/policy-viewer/policy.html?policyUUID=5188c538-ac62-4017-8a81-2d24442bbd38">Terms and Conditions</Link>
      <Divider component='span' orientation="vertical" variant="middle" flexItem style={{ marginRight: 5, marginLeft: 5 }} />
      <Link target="_blank" href="https://app.termly.io/policy-viewer/policy.html?policyUUID=7e8853f7-cdfa-4889-bcdc-f123c19d063e">Privacy Policy</Link>
    </Typography>
  );
}


function App() {
  const [value, setValue] = useState(0);
  const [selectedTab, setSelectedTab] = useState(-1);
  const [hasTabBeingSelected, setHasTabBeingSelected] = useState(false);
  const [authorSearch, setAuthorSearch] = useState('');

  let [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth() 
 
  //useVersionCheck(process.env.REACT_APP_VERSION!);

  useEffect(() => {
    
    if (searchParams.get('section') !== null) {
      if (searchParams.get('section') === "explorer") {
        setSelectedTab(3)
        setHasTabBeingSelected(true)
      }
    }
    let requestedAuthorSearch = searchParams.get('author')
    if (requestedAuthorSearch !== null) {
      setSelectedTab(1)
      setHasTabBeingSelected(true)
      setAuthorSearch(requestedAuthorSearch)
    }
  }, [])

  useEffect(() => {
    if (value === 1) {
      scrollToSection("article-analysis");
    } else {
      scrollToSection("author-explorer");
    }
  }, [value]);

  return (
    <>
      {(searchParams.get('embedded') === null) &&
        <ResponsiveAppBar
          selectTab={(newValue: number) => { setSelectedTab(newValue); setHasTabBeingSelected(true) }}
        />
      }
      <div className="App">
        <Grid container
          spacing={0}
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{ minHeight: '50vh' }}
        >
          <Routes>
            <Route path='/argos' element={
              <>
                {!currentUser &&
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack
                      direction="column"
                      component="main"
                      sx={[
                        {
                          justifyContent: 'space-between',
                          height: { xs: 'auto', md: '100%' },
                        },
                        (theme) => ({
                          marginTop: 1,
                          backgroundImage:
                            'radial-gradient(ellipse at 70% 51%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
                          backgroundSize: 'cover',
                          ...theme.applyStyles('dark', {
                            backgroundImage:
                              'radial-gradient(at 70% 51%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
                          }),
                        }),
                      ]}
                    >
                      <Stack
                        direction={{ xs: 'column-reverse', md: 'row' }}
                        sx={{
                          justifyContent: 'center',
                          gap: { xs: 6, sm: 12 },
                          p: { xs: 2, sm: 4 },
                          m: 'auto',
                        }}
                      >
                        <LoginSideContent />
                        <LoginForm />
                      </Stack>
                    </Stack>
                  </LocalizationProvider>
                }
                {currentUser &&
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <ArgosDashboardPage
                      selectedValue={selectedTab}
                      setSelectedValue={setSelectedTab}
                      authorSearchRequested={authorSearch}
                    /></LocalizationProvider>}
              </>
            } />
            <Route path='/argos/doi'>
              <Route path="*" element={
                <>
                  {!hasTabBeingSelected && <DoiPanel />}
                  {hasTabBeingSelected &&
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <ArgosDashboardPage
                        selectedValue={selectedTab}
                        setSelectedValue={(newValue: number) => { setSelectedTab(newValue); setHasTabBeingSelected(true) }}
                        authorSearchRequested={authorSearch}
                      /></LocalizationProvider>}
                </>} />
            </Route>
            <Route path='/argos/register' element={
              <RegistrationPage />
            } />
            <Route path='/argos/subscribe' element={
              <>
                {!currentUser &&
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack
                      direction="column"
                      component="main"
                      sx={[
                        {
                          justifyContent: 'space-between',
                          height: { xs: 'auto', md: '100%' },
                        },
                        (theme) => ({
                          marginTop: 1,
                          backgroundImage:
                            'radial-gradient(ellipse at 70% 51%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
                          backgroundSize: 'cover',
                          ...theme.applyStyles('dark', {
                            backgroundImage:
                              'radial-gradient(at 70% 51%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
                          }),
                        }),
                      ]}
                    >
                      <Stack
                        direction={{ xs: 'column-reverse', md: 'row' }}
                        sx={{
                          justifyContent: 'center',
                          gap: { xs: 6, sm: 12 },
                          p: { xs: 2, sm: 4 },
                          m: 'auto',
                        }}
                      >
                        <LoginSideContent />
                        <LoginForm />
                      </Stack>
                    </Stack>
                  </LocalizationProvider>
                }
                {currentUser && !hasTabBeingSelected &&
                  <SubscriptionPage
                    setSelectedValue={(newValue: number) => { setSelectedTab(newValue); setHasTabBeingSelected(true) }}
                  />}
                {currentUser && hasTabBeingSelected &&
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <ArgosDashboardPage
                      authorSearchRequested={authorSearch}
                      selectedValue={selectedTab}
                      setSelectedValue={(newValue: number) => { setSelectedTab(newValue); setHasTabBeingSelected(true) }}
                    /></LocalizationProvider>}
              </>} />
            <Route path='/argos/subscribe/pro-individual-plan' element={
              <>
                {!currentUser &&
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack
                      direction="column"
                      component="main"
                      sx={[
                        {
                          justifyContent: 'space-between',
                          height: { xs: 'auto', md: '100%' },
                        },
                        (theme) => ({
                          marginTop: 1,
                          backgroundImage:
                            'radial-gradient(ellipse at 70% 51%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
                          backgroundSize: 'cover',
                          ...theme.applyStyles('dark', {
                            backgroundImage:
                              'radial-gradient(at 70% 51%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
                          }),
                        }),
                      ]}
                    >
                      <Stack
                        direction={{ xs: 'column-reverse', md: 'row' }}
                        sx={{
                          justifyContent: 'center',
                          gap: { xs: 6, sm: 12 },
                          p: { xs: 2, sm: 4 },
                          m: 'auto',
                        }}
                      >
                        <LoginSideContent />
                        <LoginForm />
                      </Stack>
                    </Stack>
                  </LocalizationProvider>
                }
                {currentUser && !hasTabBeingSelected &&
                  <SubscribeForm productPricePerMonth='10' productName='Argos Pro' productId='prod_RFqIk79lEN6z5I' addFreeNotice={true}
                  annualProductId='prod_RFqIk79lEN6z56' productPricePerAnnum='100'
                />
                }
                {currentUser && hasTabBeingSelected &&
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <ArgosDashboardPage
                      authorSearchRequested={authorSearch}
                      selectedValue={selectedTab}
                      setSelectedValue={(newValue: number) => { setSelectedTab(newValue); setHasTabBeingSelected(true) }}
                    /></LocalizationProvider>}
              </>} />
            <Route path='/argos/stats-dashboard' element={
              <HistoricalDashboard />
            } />
            <Route path='/argos/retraction-dashboard' element={
              <HistoricalDashboard />
            } />
            <Route path='/argos/retraction-by-year' element={
              <RetractionPerYearLinearCharts />
            } />
            <Route path='/argos/retraction-by-imprint' element={
              <>
                {!hasTabBeingSelected && <PublisherSummaryBarChart />}
                {hasTabBeingSelected &&
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <ArgosDashboardPage
                      authorSearchRequested={authorSearch}
                      selectedValue={selectedTab}
                      setSelectedValue={(newValue: number) => { setSelectedTab(newValue); setHasTabBeingSelected(true) }}
                    /></LocalizationProvider>}
              </>} />
            <Route path='/argos/retraction-by-journal' element={
              <PublicJournalSummaryTable />
            } />
          </Routes>
          <Grid item xs={12} marginBottom={2} padding={2}>
            <Copyright />
          </Grid>
        </Grid></div>
    </>
  );
}

export default App;